import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl, MissingTranslationError, ReactIntlError } from 'react-intl';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { Route, Router, Switch } from 'react-router-dom';
import * as Sentry from '@sentry/react';

import { FullViewSpinner } from '@provider-portal/common/components/FullViewSpinner/FullViewSpinner';
import { CommonApplicationServices } from '@provider-portal/common/services/CommonApplicationServices';
import { ProviderPortalNavigationService } from '@provider-portal/common/services/ProviderPortalNavigationService';
import { ErrorReporter } from '@provider-portal/common/utils/errorReporting';
import { AppRoutes, AuthenticationRoutes, ProtectedRoutes } from 'AppRoutes';
import { goBack, push, replace } from 'redux-first-history';
import { History } from 'history';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { NetworkRequestState, selectIsUserLoggedIn } from '@bolteu/redux-session';

import {
  requestAccountInfoAsyncAction,
  selectAccountRequestState,
  selectProviderAction,
  selectSelectedProvider,
} from 'rdx/account';
import { showNotificationAction } from 'rdx/notifications';
import { selectAccountId } from 'rdx/session/selectors';
import {
  selectTargetingParameters,
  selectIsTargetingParametersRequestFinished,
} from 'rdx/targetingParameters/selectors';
import { RemoteIntlProvider, setGlobalIntl, getAppLocale } from 'utils/translations';
import { useBrazeChangeUserOnMount } from 'hooks/useBrazeChangeUserOnMount';

import { Notifications } from 'components/Notifications/Notifications';
import { ProviderPortalModuleManifest } from 'root/ProviderPortalApp';
import { LoginWithToken } from 'screens/Login/LoginWithToken';
import { Logout } from 'screens/Login/Logout';
import { SupportCaseRedirect } from 'screens/SupportCaseRedirect/SupportCaseRedirect';

import { CommonApplicationServicesContext } from '@provider-portal/common/services/ContextProviders';

interface AppContainerProps {
  history: History;
  commonAppServices: CommonApplicationServices;
  moduleManifests: ProviderPortalModuleManifest[];
  navigationService: ProviderPortalNavigationService;
  children: React.ReactNode;
}

const DummyComponentToSetGlobalIntl = (): null => {
  const intl = useIntl();
  const dispatch = useDispatch();

  // dirty hack that allows us to use intl in epics.
  useEffect(() => {
    setGlobalIntl(intl);
  }, [intl, dispatch]);

  return null;
};

/**
 * TanStack Query client for app-level widgets. Module micro-frontends each have their own.
 */
const shellQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

export function AppContainer({
  history,
  moduleManifests,
  commonAppServices,
  navigationService,
  children: moduleContent,
}: AppContainerProps) {
  const { accountService } = commonAppServices;

  const store = useStore();
  const accountId = useSelector(selectAccountId);

  const changeProviderSubject = useMemo(
    () => accountService.getChangeProviderSubject$(),
    [accountService],
  );
  const showNotificationSubject = useMemo(
    () => accountService.getShowNotificationSubject$(),
    [accountService],
  );
  const refreshAccountInfoSubject = useMemo(
    () => accountService.getRefreshAccountInfoSubject$(),
    [accountService],
  );
  const isUserLoggedIn = useSelector(selectIsUserLoggedIn);
  const accountRequestState = useSelector(selectAccountRequestState);

  const [translationsRequestState, setTranslationsRequestState] = useState(
    NetworkRequestState.unset,
  );
  const selectedProvider = useSelector(selectSelectedProvider);
  const dispatch = useDispatch();
  const isTargetingParametersRequestFinished = useSelector(
    selectIsTargetingParametersRequestFinished,
  );
  const targetingParameters = useSelector(selectTargetingParameters);

  useBrazeChangeUserOnMount(
    commonAppServices,
    selectedProvider,
    isTargetingParametersRequestFinished,
  );

  useEffect(() => {
    if (!accountId) {
      return;
    }

    Sentry.setUser({
      id: String(accountId),
      username: `provider:${selectedProvider?.providerId ?? 0}`,
    });
  }, [accountId, commonAppServices, selectedProvider]);

  useEffect(() => {
    if (isUserLoggedIn) {
      dispatch(requestAccountInfoAsyncAction.request());
    }
  }, [dispatch, isUserLoggedIn]);

  // Send a Portal version to Mixpanel user profile properties
  useEffect(() => {
    if (accountId && isTargetingParametersRequestFinished) {
      // TODO(DPX-3850): Clean up.
      commonAppServices.mixpanelService.setUserProfile({
        'Portal Version': 'V2',
      });
    }
  }, [accountId, isTargetingParametersRequestFinished, commonAppServices]);

  const switchProvider = useCallback(
    (linkProviderId: number) => {
      dispatch(selectProviderAction(linkProviderId));
    },
    [dispatch],
  );

  useEffect(() => {
    const providerSubject = changeProviderSubject.subscribe({
      next: (linkProviderId) => switchProvider(linkProviderId),
    });
    const notificationSubject = showNotificationSubject.subscribe({
      next: (intlId: string) => dispatch(showNotificationAction({ intlId })),
    });
    const refreshAccountInfoSubscription = refreshAccountInfoSubject.subscribe({
      next: () => dispatch(requestAccountInfoAsyncAction.request()),
    });

    return () => {
      notificationSubject.unsubscribe();
      providerSubject.unsubscribe();
      refreshAccountInfoSubscription.unsubscribe();
    };
  }, [
    changeProviderSubject,
    switchProvider,
    dispatch,
    showNotificationSubject,
    refreshAccountInfoSubject,
  ]);

  const handleTranslationsSuccess = () => {
    setTranslationsRequestState(NetworkRequestState.success);
  };

  const handleTranslationsFailure = () => {
    setTranslationsRequestState(NetworkRequestState.failure);
  };

  const handleTranslationError = (err: ReactIntlError) => {
    if (err instanceof MissingTranslationError) {
      ErrorReporter.captureMessage(
        `Missing translation: "${err.descriptor?.id}" for locale "${getAppLocale()}"`,
        { level: 'warning' },
      );
    }
  };

  const publicRoutes = moduleManifests.map((manifest) => {
    if (!manifest.isPublic) {
      return null;
    }
    return (
      <Route path={`/${manifest.moduleName}(/*)`} key={manifest.moduleName}>
        {moduleContent}
      </Route>
    );
  });

  useEffect(() => {
    const subscripton = navigationService.onNavigationCall((navCall) => {
      if (navCall.type === 'navigateTo' && navCall.url !== undefined) {
        store.dispatch(push(navCall.url));
      } else if (navCall.type === 'replaceWith' && navCall.url !== undefined) {
        store.dispatch(replace(navCall.url));
      } else if (navCall.type === 'goBack') {
        store.dispatch(goBack());
      }
    });
    return () => subscripton.unsubscribe();
  }, [navigationService, store]);

  const isTranslationsRequestFinished =
    translationsRequestState !== NetworkRequestState.unset &&
    translationsRequestState !== NetworkRequestState.waiting;

  const isUserInitializationFinished =
    accountRequestState === NetworkRequestState.success || !isUserLoggedIn;
  return (
    <RemoteIntlProvider
      onTranslationsFetchSuccess={handleTranslationsSuccess}
      onTranslationsFetchError={handleTranslationsFailure}
      onError={handleTranslationError}
    >
      <CommonApplicationServicesContext.Provider value={commonAppServices}>
        <QueryClientProvider client={shellQueryClient}>
          <DummyComponentToSetGlobalIntl />
          <Notifications />
          {isTranslationsRequestFinished &&
          isUserInitializationFinished &&
          (isUserLoggedIn ? isTargetingParametersRequestFinished : true) ? (
            <Router history={history}>
              <Switch>
                {publicRoutes}
                <Route path={AppRoutes.AUTH_WITH_TOKEN} key={AppRoutes.AUTH_WITH_TOKEN}>
                  <LoginWithToken />
                </Route>
                <Route path={AppRoutes.LOGOUT} key={AppRoutes.LOGOUT} exact>
                  <Logout />
                </Route>
                <Route path={AppRoutes.SUPPORT_REDIRECT} key={AppRoutes.SUPPORT_REDIRECT} exact>
                  <SupportCaseRedirect />
                </Route>

                {isUserLoggedIn ? (
                  <ProtectedRoutes
                    moduleManifests={moduleManifests}
                    targetingParameters={targetingParameters}
                  >
                    {' '}
                    {moduleContent}{' '}
                  </ProtectedRoutes>
                ) : (
                  <AuthenticationRoutes />
                )}
              </Switch>
            </Router>
          ) : (
            <FullViewSpinner />
          )}
        </QueryClientProvider>
      </CommonApplicationServicesContext.Provider>
    </RemoteIntlProvider>
  );
}
